import { useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { ChevronRight, Loader2 } from "lucide-react";
import type { ToolCallDisplay } from "../lib/toolCallDisplay";
import { useToolActionLabel } from "../lib/toolActionLabel";

/**
 * Collapse a long run of agent tool calls behind a single quiet summary
 * line so the chat doesn't drown in per-step rows.
 *
 * Surface-agnostic: the caller passes its already-rendered rows as
 * `children`, so the same wrapper serves InlineChat's compact rows and
 * ChannelView's heavier `<details>` rows. At or below the threshold the
 * rows render inline unchanged; above it they collapse, default-closed,
 * behind a summary that reads live activity while a turn is in flight
 * ("Searching across sources… · 12 steps") and a static tally once it
 * settles ("23 steps · 5 failed").
 */
const COLLAPSE_THRESHOLD = 3;

/** matchMedia is absent under jsdom; guard so tests (and SSR) don't throw. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

interface ToolCallGroupProps {
  calls: ToolCallDisplay[];
  children: ReactNode;
  /**
   * Applied to the wrapper in BOTH branches so a surface's spacing
   * (padding/margin) survives the threshold crossing without a layout jump.
   */
  className?: string;
}

export function ToolCallGroup({
  calls,
  children,
  className,
}: ToolCallGroupProps) {
  const [open, setOpen] = useState(false);

  // Every hook must run before the early return below: `calls.length`
  // grows as a turn streams, so hook order must not depend on it.
  const running = calls.some((call) => call.status === "running");
  const failedCount = calls.filter((call) => call.status === "failed").length;
  const activeCall =
    [...calls].reverse().find((call) => call.status === "running") ??
    calls[calls.length - 1];
  const activeLabel = useToolActionLabel(activeCall?.tool ?? "");

  if (calls.length <= COLLAPSE_THRESHOLD) {
    return <div className={className}>{children}</div>;
  }

  const reduce = prefersReducedMotion();

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-[11px]"
        style={{
          background: "var(--print-bg)",
          border: "1px solid var(--print-border)",
          color: "var(--text-secondary)",
        }}
      >
        {running ? (
          <Loader2
            size={11}
            className="shrink-0 animate-spin"
            style={{ color: "var(--text-tertiary)" }}
          />
        ) : (
          <ChevronRight
            size={12}
            className="shrink-0 transition-transform"
            style={{
              color: "var(--text-tertiary)",
              transform: open ? "rotate(90deg)" : "none",
            }}
          />
        )}
        {running ? (
          <span
            className="min-w-0 truncate"
            style={{ color: "var(--text-primary)" }}
          >
            {activeLabel}
            <span style={{ color: "var(--text-tertiary)" }}>…</span>
          </span>
        ) : null}
        <span
          className={running ? "ml-auto shrink-0 text-[10px]" : "shrink-0"}
          style={{
            color: running ? "var(--text-tertiary)" : "var(--text-primary)",
          }}
        >
          {running ? `· ${calls.length} steps` : `${calls.length} steps`}
        </span>
        {!running && failedCount > 0 ? (
          <span className="shrink-0" style={{ color: "var(--error)" }}>
            · {failedCount} failed
          </span>
        ) : null}
      </button>
      {open ? (
        <motion.div
          className="mt-1"
          initial={reduce ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.15, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      ) : null}
    </div>
  );
}
